import React from "react";
import { useT } from "@transifex/react";
import { ExerciseCreationModel, TestDetailsWord } from "p6m-p6u";
import { Exercises, ExerciseTypes } from "../../../../../../../enums/exercises";
import { sanitizeText } from "../../../../../../../helpers/TestCreation";
import WordScoreBubble from "../../../../../../../basic/wordScoreBubble/WordScoreBubble";
import MobileWordCard from "../../../detailsSubComponents/mobileWordCard/MobileWordCard";
import useBreakpoints from "../../../../../../../hooks/useBreakpoints";

import {
    ListItem,
    MobileGapCardBody,
    RemoveWord,
    RemoveWordIcon,
    TextWrapper,
    Wrapper,
    WordAndScoreWrapper,
} from "./StyledComponents";

export interface GapSentenceListProps {
    onRemoveWord: (wordId?: string) => any;
    showScores: boolean;
    currentExerciseModel: ExerciseCreationModel;
}

const MOBILE_GAP_MAX_LENGTH = 8;
const shortenGapsForMobile = (sentence: string) =>
    sentence.replace(/_{2,}/g, "_".repeat(MOBILE_GAP_MAX_LENGTH));

const GapSentenceList: React.FC<GapSentenceListProps> = (props) => {
    const { isAboveMD } = useBreakpoints();
    const t = useT();
    const t_sr_delete = t("Delete sentence", { _tags: "CreateTest,GapSentenceList,p6u" });

    const renderGapSentence = (word: TestDetailsWord) => {
        if (props.currentExerciseModel.questionMode !== ExerciseTypes.get(Exercises.FILL_GAP)) return null;
        const sentence = sanitizeText(word.gap_sentence ?? "");
        return isAboveMD ? sentence : shortenGapsForMobile(sentence);
    };

    const renderDelete = (wordId?: string) => (
        <RemoveWord
            type="button"
            aria-label={t_sr_delete}
            onClick={() => props.onRemoveWord(wordId)}
        >
            <RemoveWordIcon name={"close"} />
        </RemoveWord>
    );

    const renderDesktopRow = (w: TestDetailsWord) => (
        <WordAndScoreWrapper key={"wordListing_" + w.id}>
            <ListItem className="word-row">
                <TextWrapper>
                    {props.currentExerciseModel.questionMode === ExerciseTypes.get(Exercises.FILL_GAP) &&
                        "gap_sentence" in w &&
                        renderGapSentence(w)}
                </TextWrapper>
                {renderDelete(w.id)}
            </ListItem>
            {props.showScores && <WordScoreBubble wordId={w.id ?? ""} />}
        </WordAndScoreWrapper>
    );

    const renderMobileCard = (w: TestDetailsWord) => (
        <MobileWordCard
            key={"wordListing_" + w.id}
            wordId={w.id ?? ""}
            showScores={props.showScores}
            onDelete={() => props.onRemoveWord(w.id)}
            deleteLabel={t_sr_delete}
        >
            <MobileGapCardBody>{"gap_sentence" in w && renderGapSentence(w)}</MobileGapCardBody>
        </MobileWordCard>
    );

    return (
        <Wrapper>
            {props.currentExerciseModel.wordsInProgress?.map((w) => {
                if (!("id" in w)) return null;
                return isAboveMD ? renderDesktopRow(w) : renderMobileCard(w);
            })}
        </Wrapper>
    );
};

export default GapSentenceList;
